import { useEffect, useRef, useState } from 'react'

import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import {
  FormattedMessage,
  Tag,
  TagType,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'

import type { SaleKind } from '@/buyer-portal/helpers/sellerCollectsSaleKind'

const ns = 'Listing.tags.sellerCollects'

const Wrapper = styled.span`
  display: inline-flex;
  position: relative;
`

// A bare button around fe-core's Tag, not a restyled one: the design draws
// this chip identical to the attribute chips beside it, and wrapping the real
// component is the only version of that which cannot drift from them. The
// button exists because fe-core's Tag offers a hover tooltip only, which is
// unreadable on a phone.
const Trigger = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  padding: 0;
  white-space: nowrap;
`

type SellerCollectsTagProps = {
  /** Falls back to generic copy when the seller cannot be named. */
  sellerName?: string
  /**
   * From the seller's subscription, never the storefront config. Undefined
   * while it loads — the explainer then makes no claim about fees.
   */
  hasBuyerPremium?: boolean
  /** True when the seller delivers, so their payment covers shipping too. */
  sellerArrangesShipping?: boolean
  /**
   * Bidding or buying outright. Defaults to the auction wording, which is what
   * a caller that cannot tell should say — it claims less than the alternative.
   */
  saleKind?: SaleKind
  /**
   * Called each time the explainer opens, including a hover-open on desktop.
   * The tag stays presentational: the caller knows which surface it is on and
   * builds the event from that.
   */
  onExplainerOpened?: () => void
  'data-testid'?: string
}

/** Picks the explainer string for the fee, shipping and sale combination. */
const explainerId = (
  hasBuyerPremium: boolean | undefined,
  sellerArrangesShipping: boolean | undefined,
  saleKind: SaleKind
) => {
  // Nothing bought outright has an outcome to be conditional about, so the
  // purchase variants drop the "if you win" these open with. The branch is
  // inline rather than a variable so the orphan-string scan can still see both
  // halves of the id.
  if (hasBuyerPremium === undefined) {
    return `${ns}${saleKind === 'purchase' ? '.purchase' : ''}.explainer.unknownFee`
  }
  const fee = hasBuyerPremium ? 'feeBearing' : 'feeFree'
  return `${ns}${saleKind === 'purchase' ? '.purchase' : ''}.explainer.${fee}${sellerArrangesShipping ? 'WithShipping' : ''}`
}

/**
 * The "Seller Collects" tag and its explainer — the first place a buyer can
 * learn they pay the seller directly rather than B-Stock.
 *
 * Presentational: the seller name and fee posture are props, so the LDP, ALP
 * and orders list can all render it from whatever data they already hold.
 */
export const SellerCollectsTag = ({
  sellerName,
  hasBuyerPremium,
  sellerArrangesShipping,
  saleKind = 'bid',
  onExplainerOpened,
  'data-testid': dataTestId,
}: SellerCollectsTagProps) => {
  const { formatMessage } = useIntl()
  // Three independent sources rather than one flag: a browser fires mouseenter
  // on tap too, so a single flag would let hover-open and tap-toggle cancel
  // each other out and the explainer would never open on a phone.
  //
  // The trade-off is on desktop: clicking twice while the cursor is still over
  // the tag leaves it open, because hover is still true. Moving away closes it.
  // Deliberate — hover is the dominant desktop affordance, and letting a click
  // override it would mean a click could dismiss something the pointer is
  // actively asking to see.
  const [isPinned, setIsPinned] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  // The atom positions against a real element, so the trigger is held as
  // state rather than a ref — a ref would not re-render it into place.
  const [triggerEl, setTriggerEl] = useState<HTMLButtonElement | null>(null)
  const wrapperRef = useRef<HTMLSpanElement>(null)
  // Tapping a button also focuses it. Counting that as keyboard focus would
  // hold the explainer open forever on a phone, since the second tap only
  // clears the pin and nothing ever blurs.
  const focusedByPointer = useRef(false)

  const isOpen = isPinned || isHovered || isFocused
  const wasOpen = useRef(false)

  // Reported on the transition, not on every render while it stays open, and
  // not on close: opening it twice is two events, holding it open is one.
  useEffect(() => {
    if (isOpen && !wasOpen.current) onExplainerOpened?.()
    wasOpen.current = isOpen
  }, [isOpen, onExplainerOpened])

  useEffect(() => {
    if (!isOpen) return

    const close = () => {
      setIsPinned(false)
      setIsHovered(false)
      setIsFocused(false)
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        close()
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const explainerContent = (
    <FormattedMessage
      id={explainerId(hasBuyerPremium, sellerArrangesShipping, saleKind)}
      values={{
        sellerName: sellerName || formatMessage({ id: `${ns}.theSeller` }),
      }}
    />
  )

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        ref={setTriggerEl}
        type="button"
        aria-expanded={isOpen}
        data-testid={dataTestId}
        onClick={() => setIsPinned((pinned) => !pinned)}
        onPointerDown={() => {
          focusedByPointer.current = true
        }}
        // Guarded on a real mouse: a tap also fires these, and treating that as
        // hover would cancel the tap that follows it.
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') setIsHovered(true)
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') setIsHovered(false)
        }}
        onFocus={() => {
          if (!focusedByPointer.current) setIsFocused(true)
          focusedByPointer.current = false
        }}
        onBlur={() => {
          setIsFocused(false)
          focusedByPointer.current = false
        }}
      >
        <Tag type={TagType.info}>
          <FormattedMessage id={`${ns}.label`} />
        </Tag>
      </Trigger>
      {/* fe-core's atom unstyled: it already portals, flips and wires
          `aria-describedby`, and reads like every other tooltip in the app.
          Controlled so the tap-to-pin logic above owns when it opens. */}
      <Tooltip
        referenceElement={triggerEl}
        isOpen={isOpen}
        placement="bottom-start"
      >
        {explainerContent}
      </Tooltip>
    </Wrapper>
  )
}
